import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_PARTNER_FIRST,
  ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST,
} from "../../../../utils/constants";
import { useTranslationApp } from "../../../../lib/i18next";
import { useSelectorApp } from "../../../../lib/redux";
import {
  ADS_CAMPAIGN_META_PARTNERSHIP_AD_SECTION,
  ADS_CAMPAIGN_META_PARTNERSHIP_AD_TOGGLE,
  ADS_CAMPAIGN_META_PARTNERSHIP_APPROVAL_WARNING,
  ADS_CAMPAIGN_META_PARTNERSHIP_IDENTITY_ORDER,
  ADS_CAMPAIGN_META_PARTNERSHIP_ORDER_FIRST,
  ADS_CAMPAIGN_META_PARTNERSHIP_PARTNER_NAME_FALLBACK,
  ADS_CAMPAIGN_META_PARTNERSHIP_SPONSOR_PAGE,
  FIELD_REQUIRED,
} from "../../../../i18n/keysTranslations";
import CardContainer from "../../../Card/CardContainer";
import SelectPageFacebook from "../../../Meta/SelectPageFacebook";
import Select from "../../../Form/Select";
import Switch from "../../../Form/Switch";

const MetaPartnershipAdConfig = ({
  basicPath,
  basicPathAdGroup,
  platformData,
  control,
  errors,
  isGeneralDisabledFields,
  onGeneratePreview,
}) => {
  const { watch, setValue } = useFormContext();
  const { t } = useTranslationApp();
  const { integrations } = useSelectorApp((state) => state.user);

  const destinationTypeAdGroup = watch(`${basicPathAdGroup}.destinationType`);
  const isPostAd = watch(`${basicPath}.isPostAd`);
  const isPartnershipAdField = watch(`${basicPath}.isPartnershipAd`);
  const sponsorPageID = watch(`${basicPath}.sponsorPageID`);
  const language = watch("language");

  const showPartnershipAdCard =
    destinationTypeAdGroup !==
      ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION && !isPostAd;

  const getMetaPageNameById = (pageId) => {
    if (!pageId || !platformData?.integrationID) {
      return "";
    }
    const page =
      integrations?.facebook?.[platformData.integrationID]?.pages?.[
        String(pageId)
      ];
    return page?.name?.trim() || "";
  };

  const resolveSponsorInstagramIdForPage = (pageId) => {
    if (!pageId || !platformData?.integrationID) {
      return "";
    }
    const instagramAccounts =
      integrations?.facebook?.[platformData.integrationID]?.pages?.[
        String(pageId)
      ]?.instagramAccounts || {};
    const excludeSet = new Set(
      platformData?.instagramID ? [String(platformData.instagramID)] : []
    );
    const accounts = Object.values(instagramAccounts).filter(
      (account) => account?.id && !excludeSet.has(String(account.id))
    );
    return accounts[0]?.id ? String(accounts[0].id) : "";
  };

  const partnershipOrderOptions = useMemo(
    () => [
      {
        value: ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST,
        label: t(ADS_CAMPAIGN_META_PARTNERSHIP_ORDER_FIRST, {
          name: getMetaPageNameById(platformData?.pageID),
        }),
      },
      {
        value: ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_PARTNER_FIRST,
        label: t(ADS_CAMPAIGN_META_PARTNERSHIP_ORDER_FIRST, {
          name:
            getMetaPageNameById(sponsorPageID) ||
            t(ADS_CAMPAIGN_META_PARTNERSHIP_PARTNER_NAME_FALLBACK),
        }),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      platformData?.pageID,
      sponsorPageID,
      platformData?.integrationID,
      integrations,
      language,
    ]
  );

  useEffect(() => {
    if (!isPartnershipAdField || !sponsorPageID) {
      return;
    }
    const sponsorInstagramID = resolveSponsorInstagramIdForPage(sponsorPageID);
    const currentSponsorInstagramID = watch(`${basicPath}.sponsorInstagramID`);
    if (
      String(currentSponsorInstagramID || "") === String(sponsorInstagramID)
    ) {
      return;
    }
    setValue(`${basicPath}.sponsorInstagramID`, sponsorInstagramID, {
      shouldDirty: true,
    });
    onGeneratePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPartnershipAdField,
    sponsorPageID,
    platformData?.integrationID,
    integrations,
    basicPath,
  ]);

  useEffect(() => {
    if (showPartnershipAdCard) return;
    if (!isPartnershipAdField) return;

    setValue(`${basicPath}.isPartnershipAd`, false);
    setValue(
      `${basicPath}.partnershipOrderType`,
      ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST,
      { shouldValidate: true, shouldDirty: true }
    );
    setValue(`${basicPath}.sponsorPageID`, "");
    setValue(`${basicPath}.sponsorInstagramID`, "");
  }, [showPartnershipAdCard, isPartnershipAdField, basicPath, setValue]);

  if (!showPartnershipAdCard) {
    return null;
  }

  return (
    <Grid item xs={12}>
      <CardContainer
        title={
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t(ADS_CAMPAIGN_META_PARTNERSHIP_AD_SECTION)}
          </Typography>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name={`${basicPath}.isPartnershipAd`}
              control={control}
              render={({ field }) => (
                <Switch
                  disabled={isGeneralDisabledFields}
                  isActive={Boolean(field.value)}
                  labelActive={ADS_CAMPAIGN_META_PARTNERSHIP_AD_TOGGLE}
                  labelDeactivate={ADS_CAMPAIGN_META_PARTNERSHIP_AD_TOGGLE}
                  onChangeIsActive={(v) => {
                    field.onChange(v);
                    if (v) {
                      setValue(`${basicPath}.partnershipOrderType`, "");
                    } else {
                      setValue(
                        `${basicPath}.partnershipOrderType`,
                        ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST,
                        { shouldValidate: true, shouldDirty: true }
                      );
                      setValue(`${basicPath}.sponsorPageID`, "");
                      setValue(`${basicPath}.sponsorInstagramID`, "");
                    }
                    onGeneratePreview();
                  }}
                />
              )}
            />
          </Grid>
          {isPartnershipAdField && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ mb: 1 }} variant="body2">
                    {t(ADS_CAMPAIGN_META_PARTNERSHIP_SPONSOR_PAGE)}
                  </Typography>
                  <SelectPageFacebook
                    control={control}
                    errors={errors}
                    name={`${basicPath}.sponsorPageID`}
                    label=""
                    integrationID={platformData?.integrationID}
                    adAccountID={platformData?.adAccountID}
                    excludePageIds={
                      platformData?.pageID ? [platformData.pageID] : []
                    }
                    disableAutoSelect
                    rules={{
                      required: false,
                    }}
                    onChangeAction={(page) => {
                      const igId = page?.id
                        ? resolveSponsorInstagramIdForPage(page.id)
                        : "";
                      setValue(`${basicPath}.sponsorInstagramID`, igId, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      onGeneratePreview();
                    }}
                  />
                  <Alert
                    severity="warning"
                    sx={{
                      mt: 1.5,
                      alignItems: "flex-start",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor: "#F5A623",
                      backgroundColor: "#FFF8E6",
                      color: "#7A4F01",
                      "& .MuiAlert-icon": {
                        color: "#F5A623",
                        mt: 0.15,
                      },
                      "& .MuiAlert-message": {
                        p: 0,
                        fontSize: "0.8rem",
                        lineHeight: 1.45,
                      },
                    }}
                  >
                    {t(ADS_CAMPAIGN_META_PARTNERSHIP_APPROVAL_WARNING, {
                      name:
                        getMetaPageNameById(sponsorPageID) ||
                        t(ADS_CAMPAIGN_META_PARTNERSHIP_PARTNER_NAME_FALLBACK),
                    })}
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ mb: 1 }} variant="body2">
                    {t(ADS_CAMPAIGN_META_PARTNERSHIP_IDENTITY_ORDER)}
                  </Typography>
                  <Select
                    disabled={isGeneralDisabledFields}
                    variant="filled"
                    label=""
                    name={`${basicPath}.partnershipOrderType`}
                    fullWidth={true}
                    control={control}
                    errors={errors}
                    doTranslate={false}
                    displayEmpty={true}
                    showEmptyOption={true}
                    options={partnershipOrderOptions}
                    actionsChange={onGeneratePreview}
                    rules={{
                      validate: (value) => {
                        if (!isPartnershipAdField) {
                          return true;
                        }
                        if (
                          value ===
                            ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_SELF_FIRST ||
                          value ===
                            ADS_CAMPAIGN_META_PARTNERSHIP_AD_ORDER_PARTNER_FIRST
                        ) {
                          return true;
                        }
                        return t(FIELD_REQUIRED);
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </CardContainer>
    </Grid>
  );
};

export default MetaPartnershipAdConfig;
